import { useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { FromURL } from '../SourceMaterialModal/ContentSourcePicker/FromURL'
import { FromText } from '../SourceMaterialModal/ContentSourcePicker/FromText'
import { FromFile } from '../SourceMaterialModal/ContentSourcePicker/FromFile'
import { MainSelectionScreen } from './MainSelectionScreen'
import { useSourceMaterialModal } from './useSourceMaterialModal'
import { SCREEN_TITLES, type ScreenType } from './constants'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { Input } from 'almanac-ui/TextInputField/Input'
import type {
  BrandKitSource,
  KnowledgeBaseSourceMaterial,
  PlainText,
  Website,
  AlmanacFileSource,
  SourceMaterialsMediaObject,
} from 'types'
import Styles from './SourceMaterialModalV2.module.scss'

export interface SourceMaterialModalV2Props {
  onHandleSourceMaterialSuccess?: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  onBrandKitSourceSuccess?: (brandKitSource: BrandKitSource) => void
  onSuccess?: (
    source: PlainText | Website | AlmanacFileSource | SourceMaterialsMediaObject,
    fileName?: string,
  ) => void | Promise<void>
  brandKitId?: number
  initialScreen?: ScreenType
}

export const SourceMaterialModalV2 = ({
  onHandleSourceMaterialSuccess,
  onBrandKitSourceSuccess,
  onSuccess,
  brandKitId,
  initialScreen,
}: SourceMaterialModalV2Props) => {
  const { closeModal } = useModals()
  const [currentScreen, setCurrentScreen] = useState<ScreenType | null>(initialScreen || null)
  const [showError, setShowError] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [scrapeImages, setScrapeImages] = useState(true)
  const [followLinks, setFollowLinks] = useState(false)

  const { url, setUrl, text, setText, fileContent, setFileContent, name, setName, isValid, isSubmitting, submit } =
    useSourceMaterialModal({
      currentScreen,
      brandKitId,
      onHandleSourceMaterialSuccess,
      onBrandKitSourceSuccess,
      onSuccess,
      onError: () => {
        setShowError(true)
        setUrl('')
      },
    })

  const handleBack = () => {
    setCurrentScreen(null)
    setShowError(false)
  }

  const handleAnalyze = async () => {
    if (!isValid) {
      setShowError(true)
      return
    }

    await submit(currentScreen, { url, text, file, fileContent, name, scrapeImages, followLinks })
  }

  const getSubmitButtonLabel = (screen: ScreenType | null) => {
    if (!brandKitId) {
      if (screen === 'file') return 'Analyze and Create'
      return 'Analyze'
    }
    return 'Add Source Material'
  }

  const getSubmittingLabel = () => {
    if (!brandKitId) return 'Analyzing...'
    return 'Adding...'
  }

  const getTitle = () => {
    if (!currentScreen) return 'Add more context'
    return SCREEN_TITLES[currentScreen]
  }

  const nameField = brandKitId ? (
    <div className={Styles.nameField}>
      <Input
        name="source-name"
        placeholder="Name this source material..."
        size="lg"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        errorMessage={showError && !name ? 'Required' : undefined}
        isValid={!(showError && !name)}
        aria-label="Source material name"
        disabled={isSubmitting}
      />
    </div>
  ) : null

  return (
    <Modal.Root size="sm" blaze bodyClassName={Styles.modalBody}>
      <Modal.Header title={getTitle()} onClose={closeModal} />

      <Modal.Content withoutFooter={!currentScreen}>
        {!currentScreen && <MainSelectionScreen onSelect={setCurrentScreen} />}

        {currentScreen === 'website' && (
          <div className={Styles.screenContent}>
            <FromURL
              url={url}
              onChange={(newUrl) => {
                setUrl(newUrl)
              }}
              showError={showError}
              autoFocus
              isDisabled={isSubmitting}
            >
              <div className={Styles.scrapeImagesCheckbox}>
                <Checkbox isSelected={scrapeImages} onChange={setScrapeImages} isDisabled={isSubmitting}>
                  Extract images and include them in your media library
                </Checkbox>
                <Checkbox isSelected={followLinks} onChange={setFollowLinks} isDisabled={isSubmitting}>
                  Follow links on this page to gather additional content and images
                </Checkbox>
              </div>
            </FromURL>
          </div>
        )}

        {currentScreen === 'text' && (
          <div className={Styles.screenContent}>
            <FromText sampleText={text} showError={showError} onChange={setText} autoFocus />
            {nameField}
          </div>
        )}

        {currentScreen === 'file' && (
          <div className={Styles.screenContent}>
            <FromFile
              onChange={(content, selectedFile) => {
                setFile(selectedFile)
                setFileContent(content || '')
              }}
              showError={showError}
            />
          </div>
        )}
      </Modal.Content>

      {currentScreen && (
        <Modal.Footer blaze>
          <Modal.FooterContent slot="left">
            <Modal.FooterButton variant="tertiary" onPress={handleBack} isDisabled={isSubmitting}>
              Back
            </Modal.FooterButton>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton variant="primary" onPress={handleAnalyze} isDisabled={!isValid || isSubmitting}>
              {isSubmitting ? getSubmittingLabel() : getSubmitButtonLabel(currentScreen)}
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </Modal.Root>
  )
}
